
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { History, RotateCcw, Play, Eye, Clock } from 'lucide-react';
import { format } from 'date-fns';
import type { WorkflowRun } from '@/hooks/useWorkflowExecution';

interface WorkflowRunHistoryProps {
  onSelectRun: (runId: string) => void;
  onReplay: (runId: string) => void;
  onRollback: (runId: string) => void;
  selectedRunId: string | null;
  isRunning: boolean;
}

export const WorkflowRunHistory = ({
  onSelectRun,
  onReplay,
  onRollback,
  selectedRunId,
  isRunning,
}: WorkflowRunHistoryProps) => {
  const { data: runs, isLoading } = useQuery({
    queryKey: ['workflow-runs'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      const { data, error } = await supabase
        .from('workflow_runs')
        .select('*')
        .eq('user_id', user.id)
        .order('version', { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data || []) as WorkflowRun[];
    },
  });

  const statusBadge = (status: string) => {
    switch (status) {
      case 'completed': return <Badge className="bg-green-100 text-green-800 border-green-200">Completed</Badge>;
      case 'failed': return <Badge variant="destructive">Failed</Badge>;
      case 'running': return <Badge>Running</Badge>;
      case 'rolled_back': return <Badge variant="outline" className="text-orange-600 border-orange-300">Rolled Back</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <History className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">Workflow Run History</h3>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading runs…</p>
      ) : !runs?.length ? (
        <p className="text-sm text-muted-foreground">No workflow runs yet. Execute a workflow to see history.</p>
      ) : (
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {runs.map(run => (
            <div
              key={run.id}
              className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                selectedRunId === run.id ? 'bg-primary/5 border-primary/30' : 'hover:bg-muted/50'
              }`}
              onClick={() => onSelectRun(run.id)}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">v{run.version}</span>
                  {statusBadge(run.status)}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); onSelectRun(run.id); }}
                    title="View Report"
                  >
                    <Eye className="h-3 w-3" />
                  </Button>
                  {run.status === 'completed' && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); onReplay(run.id); }}
                        disabled={isRunning}
                        title="Replay"
                      >
                        <Play className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); onRollback(run.id); }}
                        disabled={isRunning}
                        title="Rollback to this version"
                      >
                        <RotateCcw className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {format(new Date(run.started_at), 'MMM d, yyyy · h:mm a')}
                </span>
                <span>{run.records_processed} records</span>
                <span>Source: {run.input_source === 'crm' ? 'CRM' : 'Replay'}</span>
              </div>
              {run.error_logs && (run.error_logs as any[]).length > 0 && (
                <p className="text-xs text-destructive mt-1">
                  {(run.error_logs as any[])[0]?.error}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
